import React from "react";
import { IParticipantLookupResponse, IParticipantSearchData } from "models/Participant.model";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Autocomplete, AutocompleteRenderInputParams, Divider, MenuItem, Paper, TextField } from "@mui/material";
import trim from "lodash/trim";
import debounce from "lodash/debounce";
import { serviceContainer } from "services/serviceContainer";
import { PersonSearchLibraries } from "services/person-search/PersonSearchService.types";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { css } from "@emotion/react";

export type PersonSearchResult = {
  identifier: any;
  personDetails: IParticipantLookupResponse;
  searchLibrary: PersonSearchLibraries;
};

type PersonSearchProps = {
  availableSearchLibraries: PersonSearchLibraries[];
  initialSearchLibrary: PersonSearchLibraries;
  onSearchCompleted: (searchResult: PersonSearchResult) => void;
};

export enum PersonSearchDisplayNames {
  ContactLibrary = "Contact Library",
  LBP = "LBP Register",
}

const PersonSearch: React.FC<PersonSearchProps> = ({
  availableSearchLibraries,
  initialSearchLibrary,
  onSearchCompleted,
}) => {
  const { t } = useTranslation();

  const [isOptionListOpen, setIsOptionListOpen] = React.useState(false);

  const [selectedSearchLibrary, setSelectedSearchLibrary] = React.useState(initialSearchLibrary);
  const [personSearchInputValue, setPersonSearchInputValue] = React.useState("");

  const [personOptions, setPersonOptions] = React.useState<IParticipantSearchData[]>([]);
  const isAutocompleteInputFocusedRef = React.useRef(false);
  const debouncedSearch = React.useMemo(
    () =>
      debounce(async (keywords: string) => {
        try {
          const options = await serviceContainer.cradle.personSearchService.search({
            library: selectedSearchLibrary,
            keywords,
          });

          // If autocomplete input is not focused anymore, we drop the result
          if (!isAutocompleteInputFocusedRef.current) {
            return;
          }

          setPersonOptions(options);
          setIsOptionListOpen(true);
        } catch (e) {
          serviceContainer.cradle.logger.error(e);
        }
      }, 1000),
    [selectedSearchLibrary]
  );

  const debouncedLookupPerson = React.useCallback((keywords: string) => debouncedSearch(keywords), [debouncedSearch]);
  const handlePersonSearchInputChange = React.useCallback(
    async (event: any, value: string, reason: string) => {
      if (!value && reason === "reset") {
        return;
      }

      setPersonSearchInputValue(value);

      if (!value || value.length < 2) {
        return;
      }

      await debouncedLookupPerson(value);
    },
    [debouncedLookupPerson]
  );

  const handlePersonSearchValueChange = React.useCallback(
    async (event: any, value: any, reason: string) => {
      setIsOptionListOpen(false);

      if (!value) {
        return;
      }
      const personDetails = await serviceContainer.cradle.personSearchService.find({
        library: selectedSearchLibrary,
        identifier: value.identifier,
      });

      onSearchCompleted({
        personDetails,
        identifier: value.identifier,
        searchLibrary: selectedSearchLibrary,
      });
    },
    [selectedSearchLibrary, onSearchCompleted]
  );

  const handlePersonSearchInputBlur = React.useCallback(
    (evt: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setIsOptionListOpen(false);

      // If input value is empty, clear the person details
      if (!trim(evt.target.value)) {
        return;
      }
    },
    []
  );

  const handleSelectionChanged = React.useCallback((event: SelectChangeEvent<unknown>) => {
    switch (event.target.value as string) {
      case PersonSearchLibraries.ContactLibrary:
        setSelectedSearchLibrary(PersonSearchLibraries.ContactLibrary);
        break;
      case PersonSearchLibraries.LBP:
        setSelectedSearchLibrary(PersonSearchLibraries.LBP);
        break;
    }
  }, []);

  const renderInput = React.useCallback(
    (params: AutocompleteRenderInputParams) => {
      return (
        <StyledAutocompleteInputContainer ref={params.InputProps.ref}>
          <TextField
            inputProps={params.inputProps}
            fullWidth={true}
            type="search"
            autoComplete="off"
            onBlur={(evt) => {
              isAutocompleteInputFocusedRef.current = false;
              const inputProps: any = params.inputProps;
              if (inputProps.onBlur) {
                inputProps.onBlur(evt);
              }

              handlePersonSearchInputBlur(evt);
            }}
            onFocus={(evt) => {
              isAutocompleteInputFocusedRef.current = true;
              const inputProps: any = params.inputProps;
              if (inputProps.onFocus) {
                inputProps.onFocus(evt);
              }
            }}
            error={false}
          />
        </StyledAutocompleteInputContainer>
      );
    },
    [handlePersonSearchInputBlur]
  );

  const getDisplayNameForLibrary = React.useCallback(
    (library: PersonSearchLibraries) => {
      switch (library) {
        case PersonSearchLibraries.ContactLibrary:
          return t(PersonSearchDisplayNames.ContactLibrary);
        case PersonSearchLibraries.LBP:
          return t(PersonSearchDisplayNames.LBP);
      }
    },
    [t]
  );

  return (
    <StyledSearchContainer data-testid={"PersonSearch"}>
      <StyledSearchTitle>{t(`Search for a person (optional)`)}</StyledSearchTitle>
      {t(`Populate details for the participant from an existing contact or from a linked registration scheme`)}
      <StyledSearchInput>
        <StyledSelect onChange={handleSelectionChanged} value={selectedSearchLibrary}>
          {availableSearchLibraries.map((library) => (
            <MenuItem value={library} key={library}>
              {getDisplayNameForLibrary(library)}
            </MenuItem>
          ))}
        </StyledSelect>
        <StyledDivider orientation="vertical" />
        <Autocomplete
          data-testid="PersonSearchAutocomplete"
          freeSolo={false}
          openOnFocus={false}
          selectOnFocus={false}
          blurOnSelect={true}
          onChange={handlePersonSearchValueChange}
          onInputChange={handlePersonSearchInputChange}
          inputValue={personSearchInputValue}
          open={isOptionListOpen}
          PaperComponent={StyledAutocompletePopupPaper}
          ListboxComponent={StyledAutocompleteListbox}
          style={{
            width: "60%",
          }}
          renderInput={renderInput}
          options={personOptions}
          getOptionLabel={(option) => {
            if (selectedSearchLibrary === PersonSearchLibraries.LBP) {
              return `${option.name.trim()} (${option.identifier})`;
            }
            return option.name.trim();
          }}
        />
      </StyledSearchInput>
    </StyledSearchContainer>
  );
};

const StyledDivider = styled(Divider)(
  ({ theme }) => css`
    height: unset;
    border-color: ${theme.palette.grey[600]};
  `
);

const StyledAutocompleteInputContainer = styled("div")(
  ({ theme }) => css`
    .MuiFilledInput-root {
      background-color: ${theme.palette.objective.light.white};
    }
  `
);

const StyledAutocompletePopupPaper = styled(Paper)(
  ({ theme }) => css`
    box-shadow: ${theme.shadows[6]};

    .MuiAutocomplete-noOptions {
      font-size: 14px;
    }
  `
);

const StyledAutocompleteListbox = styled("ul")(
  ({ theme }) => css`
    & > li {
      border-left: 4px solid transparent;
      padding-top: 8px;
      padding-bottom: 8px;
      font-size: 14px;
    }

    & > li:hover {
      border-left: 4px solid ${theme.palette.primary.main};
      background: ${theme.palette.objective.blue.light};
    }
  `
);

const StyledSelect = styled(Select)(
  ({ theme }) => css`
    position: relative;
    width: 40%;
    background: ${theme.palette.objective.light.white};
  `
);

const StyledSearchInput = styled("div")(
  ({ theme }) => css`
    display: flex;
    flex-direction: row;
    color: ${theme.palette.objective.dark.night};
    border-radius: 8px 8px 4px 4px;

    ${theme.mixins.flexGap("0")}
  `
);

const StyledSearchTitle = styled("span")(
  ({ theme }) => css`
    font-weight: 600;
    font-size: 16px;
  `
);

const StyledSearchContainer = styled("div")(
  ({ theme }) => css`
    background-color: ${theme.palette.objective.light.day};
    display: flex;
    flex-direction: column;
    ${theme.mixins.flexGap("8px")}
    padding: 16px;
    color: ${theme.palette.objective.dark.night};
  `
);

export default PersonSearch;
